import { Box, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, Text } from '@chakra-ui/react';
import { IconComp } from 'components/base/IconComp';
import { FileInfo, UPLOAD_FORM_FIELD_KEY } from 'components/modules/CustomForm';
import { Field } from 'formik';
import { InputFormField } from 'models';
import React, { ChangeEvent, FunctionComponent, useCallback, useState } from 'react';
import { withTranslations } from 'store/translations';
import { FieldType, getFormFieldType } from 'utils/form/fieldType';
import { getLabelName, isFileInValid, isUploadFieldInvalid, validateUploadField } from 'utils/form/validation';

interface UploadFieldProps {
  model: InputFormField;
  files: FileInfo[];
  setFiles: (files: FileInfo[]) => void;
}

export const UploadField: FunctionComponent<UploadFieldProps> = ({ model, files, setFiles }) => {
  const { common } = withTranslations();
  const [filesizeAlert, setFilesizeAlert] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [fileValidAlert, setFileValidAlert] = useState(
    'Each file must be under 20MB and in one of the following formats: ".doc", ".docx", ".pdf", ".txt",".rtf", ".jpg", ".jpeg", ".tif", ".xlsx" or ".png".',
  );
  const fileRegex = /^[0-9a-zA-Z_\-. ]+$/;

  const fileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const filesCopy = [...files];
      if (event.target.files) {
        const filesArr = Array.from(event.target.files);
        let totalSize = 0;
        filesArr.forEach((file) => {
          totalSize = totalSize + file.size;
          // file.size > 2500000
          //   ? setFilesizeAlert(`${file.name} - File size should not be greater than 20MB`)
          //   : setFilesizeAlert('');
          // !fileRegex.test(file.name)
          //   ? setFilesizeAlert(`${file.name} - File name should not contain special characters`)
          //   : setFilesizeAlert('');

          let fileInfo = {} as FileInfo;
          fileInfo = {
            fieldName: UPLOAD_FORM_FIELD_KEY + model.elements.name.value,
            fileName: file.name,
            file: file,
          } as FileInfo;
          const isFileValid =
            file.name.toLocaleLowerCase().includes('pdf') ||
            file.name.toLocaleLowerCase().includes('xlsx') ||
            file.name.toLocaleLowerCase().includes('txt') ||
            file.name.toLocaleLowerCase().includes('doc') ||
            file.name.toLocaleLowerCase().includes('docx') ||
            file.name.toLocaleLowerCase().includes('png') ||
            file.name.toLocaleLowerCase().includes('rtf') ||
            file.name.toLocaleLowerCase().includes('tif') ||
            file.name.toLocaleLowerCase().includes('jpg') ||
            file.name.toLocaleLowerCase().includes('jpeg');
          const isValidFileSize = file.size <= 2500000;
          const isValidFileName = fileRegex.test(file.name);

          if (file.size > 2500000) {
            setFileValidAlert(`${file.name} - File size should not be greater than 20MB`);
            setIsValid(false);
          } else if (!fileRegex.test(file.name)) {
            setFileValidAlert(`${file.name} - File name should not contain special characters`);
            setIsValid(false);
          } else if (!isFileValid) {
            setIsValid(false);
            setFileValidAlert(`Please upload only pdf/docx/doc/txt/jpg/jpeg/tif/rtf/png/xlsx files`);
          } else if (totalSize > 2500000) {
            setIsValid(false);
            setFileValidAlert(`Total file size should not exceed 20MB`);
          } else {
            setIsValid(true);
            setFilesizeAlert('');
          }
          if (!filesCopy.some((f) => JSON.stringify(f) === JSON.stringify(fileInfo))) {
            const fieldType = getFormFieldType(model);
            if (
              fieldType === FieldType.UploadMultiple ||
              (fieldType === FieldType.UploadSingle &&
                !filesCopy.some((f) => f.fieldName === fileInfo.fieldName) &&
                file)
            ) {
              // add if multiple upload or single upload empty
              if (isFileValid && isValidFileSize && isValidFileName) {
                filesCopy.push(fileInfo);
              }
            } else {
              // replace if single upload has some file
              const idx = filesCopy.findIndex((f) => f.fieldName === fileInfo.fieldName);
              if (isFileValid && isValidFileSize && isValidFileName) {
                filesCopy.splice(idx, 1, fileInfo);
              } else {
                filesCopy.splice(idx, 1);
              }
            }
          }
        });
      }

      setFiles(filesCopy);
    },
    [files, model, setFiles],
  );

  return (
    <Box>
      <Field
        name={UPLOAD_FORM_FIELD_KEY + model.elements.name.value}
        validate={model.elements.required.value[0].codename === 'yes' ?? validateUploadField(files, model)}
      >
        {({ field, form }) => (
          <FormControl
            isInvalid={
              form.touched[UPLOAD_FORM_FIELD_KEY + model.elements.name.value] &&
              (isUploadFieldInvalid(files, model) || isFileInValid(files))
            }
          >
            {model.elements.label.value !== '' && <FormLabel>{getLabelName(model)}</FormLabel>}
            <Box data-group position={'relative'}>
              <Input
                position='absolute'
                top={0}
                left={0}
                {...field}
                opacity='0'
                zIndex={10}
                type='file'
                multiple={model.elements.type.value[0].codename === 'upload_multiple'}
                accept={model.elements.allowedTypesForUploadFiles.value}
                onChange={(event) => fileChange(event)}
              />
              <Flex
                bg='gray.red'
                py='xxs'
                pr='15px'
                pl='xxs'
                alignItems='center'
                borderColor={
                  form.touched[UPLOAD_FORM_FIELD_KEY + model.elements.name.value] &&
                  (isUploadFieldInvalid(files, model) || isFileInValid(files))
                    ? 'field'
                    : 'mountain.20'
                }
                borderWidth={
                  form.touched[UPLOAD_FORM_FIELD_KEY + model.elements.name.value] &&
                  (isUploadFieldInvalid(files, model) || isFileInValid(files))
                    ? '2px'
                    : '1px'
                }
                justifyContent='space-between'
                _groupFocusWithin={{
                  borderColor: 'cyan.web',
                  borderWidth: '2px',
                }}
              >
                <Text
                  _groupHover={{
                    color: 'mountain.base',
                  }}
                  color='mountain.60'
                  bg='gray.red'
                  textStyle='mobile.body.s'
                >
                  {common('chooseFile')}
                </Text>
                <IconComp iconName={'upload'} />
              </Flex>
            </Box>
            {files.filter((file) => file.fieldName === UPLOAD_FORM_FIELD_KEY + model.elements.name.value).length >
              0 && (
              <Flex mt='xxs' flexDir='column' gap='xxs'>
                {files
                  .filter((file) => file.fieldName === UPLOAD_FORM_FIELD_KEY + model.elements.name.value)
                  .map((file, index) => {
                    return (
                      <>
                        {file.file.size >= 2500000 || !fileRegex.test(file.file.name) ? (
                          <Text className='chakra-form__error-message' color='#FF8855'>
                            {filesizeAlert}
                          </Text>
                        ) : (
                          <Flex
                            key={index}
                            py='xxs'
                            pr='15px'
                            pl='xxs'
                            bg='gray.red'
                            alignItems='center'
                            borderColor={'mountain.20'}
                            borderWidth='1px'
                            justifyContent='space-between'
                          >
                            <Text color='mountain.60' bg='gray.red' textStyle='mobile.body.s'>
                              {file.fileName}
                            </Text>
                            <Box
                              as='button'
                              type='button'
                              onClick={() => {
                                setFilesizeAlert('');
                                const filesCopy = [...files];
                                const idx = filesCopy.findIndex((f) => JSON.stringify(f) === JSON.stringify(file));
                                if (idx >= 0) {
                                  filesCopy.splice(idx, 1);
                                  setFiles(filesCopy);
                                }
                              }}
                            >
                              <IconComp iconName={'trash'} />
                            </Box>
                          </Flex>
                        )}
                      </>
                    );
                  })}
              </Flex>
            )}
            {!isValid && (
              <Text className='chakra-form__error-message' color={'#E53E3E'}>
                {fileValidAlert}
              </Text>
            )}
            <FormHelperText style={{ fontWeight: 'bold' }} textStyle='desktop.body.medium.l'>
              Each file must be under 20MB and in one of the following formats: ".doc", ".docx", ".pdf", ".txt",".rtf",
              ".jpg", ".jpeg", ".tif", ".xlsx" or ".png".
            </FormHelperText>

            {}
            <FormErrorMessage className='chakra-form__error-message'>
              {/* {form.errors[UPLOAD_FORM_FIELD_KEY + model.elements.name.value] ??
                `${model.elements.name.value} ${common(
                  'isRequired',
                )} .Please upload only pdf/docx/doc/txt/jpg/jpeg/tif/rtf/png files`} */}
              {filesizeAlert}
            </FormErrorMessage>
          </FormControl>
        )}
      </Field>
    </Box>
  );
};
